<script setup>
import { computed, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PixtaAPI from 'custom/api/pixta';
import { intlLocale, useLocoI18n } from 'custom/composables/useLocoI18n';
import PixtaOrder from './PixtaOrder.vue';
import { WITHDRAWAL_COLORS, formatDate, formatMoney } from './helpers';

// The conversation sidebar's Pixta card: who is writing (found by the channel's verified number, e-mail or widget
// login, or linked by an agent), their balances, withdrawals and orders. Agents can search and link someone when the
// channel doesn't say who it is, resend tickets and cancel an order under the buyer's own rules.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t, locale } = useLocoI18n();
const { run, isPending } = useAbortableRequest();

const customer = ref(null);
const error = ref('');
const query = ref('');
const results = ref(null);
const isSearching = ref(false);
const busyOrderId = ref('');

const lang = computed(() => intlLocale(locale.value));
const money = value => formatMoney(value, lang.value);
const date = value => formatDate(value, lang.value);
const errorMessage = requestError =>
  requestError.response?.data?.error || requestError.message;

const person = computed(() => customer.value?.person);
// Pages of Pixta's admin: users/<id>, orders/<id>, events/<slug>
const adminLink = path => `${customer.value.admin_url}/${path}`;
const identity = computed(() => customer.value?.identity || {});
const candidates = computed(
  () => results.value ?? customer.value?.people ?? []
);
const unidentified = computed(() => {
  if (identity.value.phone || identity.value.email) {
    return t('LOCO.PIXTA.NOT_FOUND', {
      who: identity.value.phone || identity.value.email,
    });
  }
  return t('LOCO.PIXTA.NO_IDENTITY');
});

const load = async () => {
  error.value = '';
  try {
    const response = await run(signal =>
      PixtaAPI.customer(props.conversationId, signal)
    );
    if (response) customer.value = response.data;
  } catch (requestError) {
    error.value = errorMessage(requestError);
  }
};

watch(
  () => props.conversationId,
  () => {
    customer.value = null;
    results.value = null;
    query.value = '';
    load();
  },
  { immediate: true }
);

const search = async () => {
  if (!query.value.trim()) return;
  isSearching.value = true;
  try {
    const { data } = await PixtaAPI.search(props.conversationId, query.value);
    results.value = data.people;
  } catch (requestError) {
    useAlert(errorMessage(requestError));
  } finally {
    isSearching.value = false;
  }
};

const link = async userId => {
  try {
    const { data } = await PixtaAPI.link(props.conversationId, userId);
    customer.value = data;
    results.value = null;
  } catch (requestError) {
    useAlert(errorMessage(requestError));
  }
};

const unlink = async () => {
  try {
    const { data } = await PixtaAPI.unlink(props.conversationId);
    customer.value = data;
  } catch (requestError) {
    useAlert(errorMessage(requestError));
  }
};

// `action` is a PixtaAPI method name (called on PixtaAPI, which its URLs need)
const act = async (order, action, done) => {
  busyOrderId.value = order.id;
  try {
    await PixtaAPI[action](props.conversationId, order.id);
    useAlert(done);
    await load();
  } catch (requestError) {
    useAlert(errorMessage(requestError));
  } finally {
    busyOrderId.value = '';
  }
};
const resend = order => act(order, 'resendTickets', t('LOCO.PIXTA.RESENT'));
const cancelOrder = order =>
  act(order, 'cancelOrder', t('LOCO.PIXTA.CANCEL.DONE'));
</script>

<template>
  <div class="flex flex-col gap-4 px-4 py-3 text-sm text-n-slate-12">
    <Spinner v-if="isPending && !customer" class="mx-auto" />
    <p v-else-if="error" role="alert" class="m-0 text-n-ruby-11">{{ error }}</p>

    <template v-else-if="person">
      <div class="flex items-start gap-3 min-w-0">
        <span
          class="flex items-center justify-center size-9 shrink-0 rounded-lg bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-ticket size-4" aria-hidden="true" />
        </span>
        <div
          class="flex flex-col gap-0.5 min-w-0 flex-1 text-xs text-n-slate-11"
        >
          <span class="flex items-center gap-1.5 min-w-0">
            <a
              :href="adminLink(`users/${person.id}`)"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-medium truncate text-n-slate-12 hover:underline"
            >
              {{ person.name }}
            </a>
            <Label
              v-if="person.producer"
              compact
              :label="t('LOCO.PIXTA.PRODUCER')"
              color="iris"
            />
          </span>
          <span class="truncate">{{ person.email }}</span>
          <span v-if="person.cellphone">{{ person.cellphone }}</span>
          <button
            v-if="identity.linked"
            type="button"
            class="self-start text-n-slate-11 hover:text-n-slate-12 hover:underline"
            @click="unlink"
          >
            {{ t('LOCO.PIXTA.UNLINK') }}
          </button>
        </div>
      </div>

      <section
        class="flex flex-col gap-1 pt-3 border-t border-n-weak text-xs text-n-slate-11"
      >
        <span>
          {{
            t('LOCO.PIXTA.BALANCE', {
              available: money(person.balance.available),
              pending: money(person.balance.not_available),
            })
          }}
        </span>
        <span v-if="person.producer_balance">
          {{
            t('LOCO.PIXTA.PRODUCER_BALANCE', {
              available: money(person.producer_balance.available),
              pending: money(person.producer_balance.not_available),
            })
          }}
          <template v-if="person.producer_balance.next_release_at">
            ·
            {{
              t('LOCO.PIXTA.NEXT_RELEASE', {
                date: date(person.producer_balance.next_release_at),
              })
            }}
          </template>
        </span>
        <ul v-if="person.withdrawals.length" class="flex flex-col gap-1 pt-1">
          <li
            v-for="withdrawal in person.withdrawals"
            :key="withdrawal.id"
            class="flex items-center justify-between gap-2"
          >
            <span class="truncate">
              {{
                t('LOCO.PIXTA.WITHDRAWAL', {
                  amount: money(withdrawal.amount),
                  date: date(withdrawal.requested_at),
                })
              }}
            </span>
            <Label
              compact
              :label="t(`LOCO.PIXTA.WITHDRAWAL_STATUS.${withdrawal.status}`)"
              :color="WITHDRAWAL_COLORS[withdrawal.status]"
            />
          </li>
        </ul>
      </section>

      <section
        v-if="person.events?.length"
        class="flex flex-col gap-1 pt-3 border-t border-n-weak text-xs"
      >
        <h4 class="m-0 text-xs font-medium text-n-slate-11">
          {{ t('LOCO.PIXTA.EVENTS') }}
        </h4>
        <a
          v-for="event in person.events"
          :key="event.id"
          :href="adminLink(`events/${event.slug}`)"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center justify-between gap-2 hover:underline"
        >
          <span class="truncate text-n-slate-12">{{ event.name }}</span>
          <span class="shrink-0 text-n-slate-11">
            {{ date(event.starts_at) }}
          </span>
        </a>
      </section>

      <section class="flex flex-col pt-3 border-t border-n-weak">
        <h4 class="m-0 text-xs font-medium text-n-slate-11">
          {{ t('LOCO.PIXTA.ORDERS', { n: person.orders_count }) }}
        </h4>
        <p
          v-if="!person.orders.length"
          class="m-0 pt-2 text-xs text-n-slate-11"
        >
          {{ t('LOCO.PIXTA.NO_ORDERS') }}
        </p>
        <div class="flex flex-col divide-y divide-n-weak">
          <PixtaOrder
            v-for="order in person.orders"
            :key="order.id"
            :order="order"
            :admin-url="customer.admin_url"
            :busy="busyOrderId === order.id"
            @resend="resend"
            @cancel="cancelOrder"
          />
        </div>
      </section>
    </template>

    <template v-else-if="customer">
      <p class="m-0 text-xs text-n-slate-11">
        {{ customer.people.length ? t('LOCO.PIXTA.SEVERAL') : unidentified }}
      </p>
      <form class="flex gap-2" @submit.prevent="search">
        <input
          v-model="query"
          type="text"
          class="!mb-0 text-sm"
          :placeholder="t('LOCO.PIXTA.SEARCH_PLACEHOLDER')"
        />
        <Button
          type="submit"
          sm
          faded
          slate
          icon="i-lucide-search"
          :is-loading="isSearching"
        />
      </form>
      <p v-if="results && !results.length" class="m-0 text-xs text-n-slate-11">
        {{ t('LOCO.PIXTA.NO_RESULTS') }}
      </p>
      <ul v-if="candidates.length" class="flex flex-col gap-2">
        <li
          v-for="candidate in candidates"
          :key="candidate.id"
          class="flex items-center justify-between gap-2"
        >
          <span class="flex flex-col min-w-0 text-xs text-n-slate-11">
            <span class="text-sm truncate text-n-slate-12">{{
              candidate.name
            }}</span>
            <span class="truncate">{{ candidate.email }}</span>
          </span>
          <Button
            type="button"
            xs
            faded
            blue
            :label="t('LOCO.PIXTA.LINK')"
            @click="link(candidate.id)"
          />
        </li>
      </ul>
    </template>
  </div>
</template>
